import { UserServiceContext } from '@authentic/loyalty-pwa/providers';
import { Button } from '@authentic/loyalty-pwa/components';
import { Fragment, FunctionalComponent, h } from 'preact';
import { useContext, useState } from 'preact/hooks';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { SignUpContainer } from '../containers/SignUpContainer';
import { TextField } from '@authentic/loyalty-pwa/components/Form';
import { ErrorMessage } from '../components/Error';
import { Link } from 'preact-router';
import { objectIsEmpty, getImageSrc, link, Typography } from '@authentic/loyalty-pwa/util';
import { breakpoints } from '@authentic/loyalty-pwa/util/variables';

const FormStyled = styled.form`
  background: #ffffff;
  text-align: left;
  margin-bottom: 50px;
`;

const FormControlStyled = styled.div`
  margin-bottom: 12px;
`;

const Wrapper = styled(SignUpContainer)`
  background-image: url(${getImageSrc(require('../assets/churro-sm.png').default)});
  background-repeat: no-repeat;
  background-position: bottom center;
  padding-bottom: 56px;

  @media screen and (min-width: ${breakpoints.sm}) {
    background-image: none;

    .white-box {
      background-image: url(${getImageSrc(require('../assets/churro-xl.png').default)});
      background-repeat: no-repeat;
      background-position: bottom center;
      padding-bottom: 100px;
    }
  }
`;

const Subtitle = styled.div`
  margin-bottom: 32px;
  text-align: center;
`;

const LinkButton = styled(Link)`
  text-decoration: none;
  button {
    cursor: pointer;
  }
`;

const SubTitleStyled = styled.div`
  text-transform: initial;
  line-height: initial;
  margin-top: 12px;
`;

type FormValues = {
  email: string;
};

export const ForgotPasswordPage: FunctionalComponent = () => {
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const userService = useContext(UserServiceContext);
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>();

  const onSubmit: any = (data: FormValues, event: { target: HTMLFormElement }) => {
    setIsLoading(true);
    userService
      ?.passwordReset({
        email: data.email,
      })
      .then((res: any) => {
        if (res.success) {
          setMessage(res.message);
          setValue('email', '');
          setTimeout(() => {
            reset();
          }, 0);
        } else {
          setError(res.message);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const emailOnFocus: any = (e: any) => {
    console.log('Event: ', e);
    setMessage('');
  }

  return (
    <div>
      <Wrapper
        pageTitle='Forgot Password'
        title={
          <Fragment>
            DON'T WORRY! <br /> WE CAN HELP.
            <Subtitle>
              <Typography variant="body2">
                <SubTitleStyled>Enter your email below and we’ll send instructions to reset your password.</SubTitleStyled>
              </Typography>
            </Subtitle>
          </Fragment>
        }
      >
        <FormStyled onSubmit={handleSubmit(onSubmit)}>
          <FormControlStyled>
            <Controller
              render={({ field }: any): any => (
                <TextField error={errors?.email} label="Email" {...field} required id="email" onFocus={emailOnFocus} />
              )}
              rules={{
                required: {
                  value: true,
                  message: 'Please enter your Email',
                },
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'Please enter valid email address',
                },
              }}
              control={control}
              name="email"
              defaultValue=""
            />
          </FormControlStyled>
          <FormControlStyled>{!!error && <ErrorMessage>{error}</ErrorMessage>}</FormControlStyled>
          {!objectIsEmpty(errors) && (
            <ErrorMessage>
              Review highlighted errors to continue
            </ErrorMessage>
          )}
          <FormControlStyled>{message}</FormControlStyled>
          <FormControlStyled>
            <Button
              ariaLabel="SEND RESET INSTRUCTIONS"
              type="submit"
              disabled={!isDirty || isLoading}
              fullWidth
            >
              SEND RESET INSTRUCTIONS
            </Button>
          </FormControlStyled>
          <FormControlStyled>
            <LinkButton href={link('/sign-in')}>
              <Button ariaLabel="RETURN TO SIGN IN" type="button" fullWidth variant="secondary">
                RETURN TO SIGN IN
              </Button>
            </LinkButton>
          </FormControlStyled>
        </FormStyled>
      </Wrapper>
    </div>
  );
};
